import { FC, useEffect, useMemo } from 'react'

import { Tinode } from '@inno-sdk/livechat-sdk'
import { AppId } from '@inno-tools/container-bridge'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { useMountApp } from 'modules/app-registry/hooks/useMountApp'
import type { LiveChatContact } from 'shared/constants/customEvents'
import { useUserContext } from 'shared/hooks/useUserContext'

interface UserChatModalProps {
  livechat: typeof Tinode
  topicId: string
  isOpen: boolean
  onClose: () => void
  contact?: LiveChatContact
  requestId?: string
  onReady?: () => void
  onError?: () => void
}

interface UserChatModalWidgetProps {
  livechat: typeof Tinode
  user: ReturnType<typeof useUserContext>['user']
  topicId: string
  isOpen: boolean
  onClose: () => void
  contact?: LiveChatContact
}

/** Mounts LiveChat V2's own 1:1 chat modal with the shared SDK instance. */
export const UserChatModal: FC<UserChatModalProps> = ({
  livechat,
  topicId,
  isOpen,
  onClose,
  contact,
  requestId,
  onReady,
  onError
}) => {
  const { user } = useUserContext()
  const { apps } = useAvailableApps()

  const appProps = useMemo<UserChatModalWidgetProps>(
    () => ({ livechat, user, topicId, isOpen, onClose, contact }),
    [livechat, user, topicId, isOpen, onClose, contact]
  )

  const { ref, isLoaded, isError } = useMountApp(
    {
      url: process.env.REACT_APP_LIVE_CHAT_V2_APP_DEV_PATH,
      scope: 'livechat_v2',
      envKey: apps[AppId.LIVE_CHAT].runtimeEnvKey,
      entryPath: './UserChatModalWidget'
    },
    appProps
  )

  useEffect(() => {
    if (!requestId) return

    if (isError || !user) {
      onError?.()
    } else if (isLoaded) {
      onReady?.()
    }
  }, [isError, isLoaded, onError, onReady, requestId, user])

  if (isError || !isLoaded || !user) return null

  return <div ref={ref} />
}
